import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { useFilter } from "@/context/FilterContext";
import { Gauge } from "lucide-react";

const DOT = { green: "bg-[#059669]", amber: "bg-[#F59E0B]", red: "bg-[#DC2626]" };
const BAR = { green: "bg-[#059669]", amber: "bg-[#F59E0B]", red: "bg-[#DC2626]" };
const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

export default function KpiStrip({ module }) {
  const { role, employee } = useFilter();
  const { data, isLoading } = useQuery({
    queryKey: ["kpi-summary", module, role, employee],
    queryFn: async () => {
      const params = { module };
      if (role) params.role = role;
      if (employee) params.employee = employee;
      return (await api.get("/kpi-summary", { params })).data;
    },
  });

  const kpis = data?.kpis || [];
  if (isLoading) return <div className="text-slatetext text-sm">Calculating KPIs…</div>;
  if (!kpis.length) return null;

  return (
    <div data-testid={`kpi-strip-${module}`}>
      <div className="flex items-center gap-2 mb-3">
        <Gauge className="w-5 h-5 text-gold" />
        <h3 className="font-heading font-bold">Auto KPIs <span className="text-xs text-slatetext font-normal">— from activity records ({data.month})</span></h3>
      </div>
      <div className="grid gap-4 mb-2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))" }}>
        {kpis.map((k) => (
          <div key={k.metric_key} className="rounded-lg border border-surface-hover bg-surface p-4" data-testid={`kpi-card-${k.metric_key}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slatetext">{k.name}</span>
              <span className={`w-2.5 h-2.5 rounded-full ${DOT[k.rag]}`} />
            </div>
            <div className="flex items-end gap-2">
              <span className="font-heading text-2xl font-extrabold">{fmt(k.month)}</span>
              <span className="text-xs text-slatetext mb-1">/ {fmt(k.target)} {k.unit}</span>
              <span className="ml-auto text-sm font-bold" style={{ color: k.rag === "green" ? "#059669" : k.rag === "amber" ? "#F59E0B" : "#DC2626" }}>{k.achievement}%</span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-navy overflow-hidden my-2">
              <div className={`h-full ${BAR[k.rag]}`} style={{ width: `${Math.min(100, k.achievement)}%` }} />
            </div>
            <div className="flex items-center gap-3 text-xs text-slatetext">
              <span>Today <b className="text-white">{fmt(k.today)}</b></span>
              <span>Week <b className="text-white">{fmt(k.week)}</b></span>
              <span>Month <b className="text-white">{fmt(k.month)}</b></span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
